import {
  Banner,
  Button,
  CheckSVG,
  CopySVG,
  DownloadSVG,
  EditHollowSVG,
} from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { Editor, EditorContent } from "@tiptap/react";
import { useCallback, useMemo, useState } from "react";

import RichTextEditor from "@/components/RichTextEditor";
import ContentLevel from "@/types/models/ContentLevel.interface";
import { User } from "@/types/models/User/v3/User.interface";
import { formatLessonPlan, getCopyHandler, getDownloadHandler } from "../utils";
import { updateStoredLesson } from "../utils/lessonPlanStorage";
import { ILessonPlanNode } from "../utils/useLessonPlanEditor/useLessonPlanEditor";
import {
  bottomEditActionButtonsCss,
  bottomWarningCss,
  collapseActionsWrapperCss,
  commonButtonCss,
  lessonPlanEditorBodyStyles,
} from "./LessonPlanContent.style";

const ButtonWithTrackEvent = withTrackEvent(Button);

interface LessonPlanContentProps {
  editor: Editor | null;
  contentId: string;
  currentLevel: ContentLevel | null | undefined;
  user: User | null | undefined;
}

const LessonPlanContent = ({
  editor,
  contentId,
  currentLevel,
  user,
}: LessonPlanContentProps) => {
  const [isEditModeActive, setIsEditModeActive] = useState(false);
  const [isTextCopied, setIsTextCopied] = useState(false);
  const [isContentEdited, setIsContentEdited] = useState(false);

  const contentWithMarks = useMemo(() => {
    return formatLessonPlan({
      lessonPlan: editor?.getJSON(),
      isEditOrDownloadMode: true,
    });
  }, [editor?.getHTML()]);

  const handleDownload = useCallback(
    getDownloadHandler({ editor, title: currentLevel?.short_title || "" }),
    [currentLevel?.short_title, editor?.getHTML()],
  );
  const handleCopy = useCallback(
    getCopyHandler({
      editor,
      onFulfilled: () => setIsTextCopied(true),
      onRejected: () => setIsTextCopied(false),
    }),
    [editor?.getHTML()],
  );
  const handleEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsEditModeActive(() => {
      if (editor && isEditModeActive) {
        editor.commands.setContent(
          formatLessonPlan({ lessonPlan: editor.getJSON() }),
        );
      }
      setIsContentEdited(isEditModeActive);
      if (isEditModeActive) {
        updateStoredLesson({
          user,
          contentId,
          lessonPlan: editor?.getJSON() as ILessonPlanNode,
        });
      }
      return !isEditModeActive;
    });
  };
  const handleCancelEdit = () => {
    setIsEditModeActive(false);
  };

  if (!editor) return null;

  const editButton = (
    <ButtonWithTrackEvent
      actionName={actionNames.start}
      objectType={objectTypes.generatedTextAsset}
      flavor={Button.flavors.muted}
      size={Button.sizes.lg}
      isFullWidth
      __cssFor={commonButtonCss}
      ariaProps={{
        "aria-label": "Edit lesson plan",
      }}
      dataProps={{
        "data-qa-selector": "edit-lesson-plan-button",
      }}
      streamProperties={{
        description: "User edited the lesson plan",
      }}
      icon={{
        alignment: Button.iconAlignments.right,
        SvgComponent: EditHollowSVG,
      }}
      onClick={handleEdit}
    >
      {"Edit lesson"}
    </ButtonWithTrackEvent>
  );

  const downloadButton = (
    <ButtonWithTrackEvent
      actionName={actionNames.download}
      objectType={objectTypes.generatedTextAsset}
      flavor={Button.flavors.muted}
      size={Button.sizes.lg}
      isFullWidth
      __cssFor={commonButtonCss}
      ariaProps={{
        "aria-label": "Download lesson plan as PDF",
      }}
      dataProps={{
        "data-qa-selector": "download-lesson-plan-button",
      }}
      streamProperties={{
        description: "User downloaded the lesson plan",
      }}
      icon={{
        alignment: Button.iconAlignments.right,
        SvgComponent: DownloadSVG,
      }}
      onClick={handleDownload}
    >
      Download as PDF
    </ButtonWithTrackEvent>
  );

  const copyButton = (
    <ButtonWithTrackEvent
      actionName={actionNames.copy}
      objectType={objectTypes.generatedTextAsset}
      flavor={Button.flavors.muted}
      size={Button.sizes.lg}
      __cssFor={commonButtonCss}
      isFullWidth
      ariaProps={{
        "aria-label": "Copy lesson plan to clipboard",
      }}
      dataProps={{
        "data-qa-selector": "copy-lesson-plan-button",
      }}
      streamProperties={{
        description: "User copied the lesson plan",
      }}
      icon={{
        SvgComponent: isTextCopied ? CheckSVG : CopySVG,
        alignment: Button.iconAlignments.right,
      }}
      onClick={handleCopy}
    >
      {isTextCopied ? "Copied" : "Copy"} to clipboard
    </ButtonWithTrackEvent>
  );

  const saveEditButton = (
    <ButtonWithTrackEvent
      actionName={actionNames.start}
      objectType={objectTypes.generatedTextAsset}
      flavor={Button.flavors.primary}
      size={Button.sizes.lg}
      __cssFor={commonButtonCss}
      ariaProps={{
        "aria-label": "Save lesson plan",
      }}
      dataProps={{
        "data-qa-selector": "save-edit-lesson-plan-button",
      }}
      streamProperties={{
        description: "User save the edited lesson plan",
      }}
      onClick={handleEdit}
    >
      {"Save edit"}
    </ButtonWithTrackEvent>
  );

  const cancelEditButton = (
    <ButtonWithTrackEvent
      actionName={actionNames.start}
      objectType={objectTypes.generatedTextAsset}
      flavor={Button.flavors.muted}
      size={Button.sizes.lg}
      __cssFor={commonButtonCss}
      ariaProps={{
        "aria-label": "Cancel edit lesson plan",
      }}
      dataProps={{
        "data-qa-selector": "cancel-edit-lesson-plan-button",
      }}
      streamProperties={{
        description: "User cancel the edited lesson plan",
      }}
      onClick={handleCancelEdit}
    >
      {"Cancel"}
    </ButtonWithTrackEvent>
  );

  return (
    <>
      {isEditModeActive ? (
        <>
          <div css={lessonPlanEditorBodyStyles}>
            <RichTextEditor
              content={contentWithMarks}
              config={{ Heading: { levels: [1, 2, 3, 4] } }}
              onChange={(editedHTML) => {
                editor.commands.setContent(editedHTML);
              }}
            />
          </div>
          <div css={bottomEditActionButtonsCss}>
            {cancelEditButton}
            {saveEditButton}
          </div>
        </>
      ) : (
        <>
          <div css={collapseActionsWrapperCss}>
            {editButton}
            {copyButton}
            {downloadButton}
          </div>
          <EditorContent editor={editor} />
        </>
      )}

      <p css={bottomWarningCss}>
        While our AI is smart, it's not perfect: please review the lesson plan
        to make sure it's just right before using it with your class.
      </p>

      {isContentEdited && (
        <Banner
          bannerType={Banner.types.success}
          body="Your edits have been updated successfully."
          isCenter={false}
          isDismissible
          onClose={() => setIsContentEdited(false)}
        />
      )}
    </>
  );
};

export default LessonPlanContent;
